import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Button, Tooltip, IconButton, CircularProgress, Alert, Chip } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import { apiClient } from '../services/apiClient';

export default function ScratchCard({ coupon, prize, couponCode, status = 'unscratched', onComplete, onRedeemed, onGoBack }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const scratchedTriggered = useRef(false);

  const [scratched, setScratched] = useState(status === 'scratched' || coupon?.status === 'REDEEMED');
  const [copied, setCopied] = useState(false);
  const [canvasLoaded, setCanvasLoaded] = useState(false);

  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);
  const [displayedCouponCode, setDisplayedCouponCode] = useState(couponCode || '');

  useEffect(() => {
    if (couponCode) {
      setDisplayedCouponCode(couponCode);
    }
  }, [couponCode]);

  useEffect(() => {
    if (status === 'scratched' || coupon?.status === 'REDEEMED') {
      setScratched(true);
      return;
    }

    const timer = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      // Draw scratch layer
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#D4A24C');
      gradient.addColorStop(0.5, '#E8C078');
      gradient.addColorStop(1, '#A9673A');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw noise/grid pattern
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 2;
      for (let i = 0; i < canvas.width; i += 20) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for (let j = 0; j < canvas.height; j += 20) {
        ctx.beginPath();
        ctx.moveTo(0, j);
        ctx.lineTo(canvas.width, j);
        ctx.stroke();
      }

      // Add overlay text - DO NOT SHOW CASHBACK TEXT PRE-SCRATCH
      ctx.fillStyle = '#0F2E2B';
      ctx.font = '700 16px "Inter", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('SCRATCH HERE TO REVEAL', canvas.width / 2, canvas.height / 2 - 10);
      ctx.font = '500 12px "Inter", sans-serif';
      ctx.fillText('🌟 UNLOCK YOUR REWARD 🌟', canvas.width / 2, canvas.height / 2 + 15);

      setCanvasLoaded(true);
    }, 150);

    return () => clearTimeout(timer);
  }, [status, coupon]);

  const triggerRedeemApi = async () => {
    const couponId = coupon?.couponId || coupon?.id || 1;
    setRedeeming(true);
    setRedeemError(null);
    try {
      const response = await apiClient.post(`/coupons/${couponId}/redeem`);
      if (response && response.data) {
        setRedeemResult(response.data);
        if (response.data.couponCode) {
          setDisplayedCouponCode(response.data.couponCode);
        }
        if (onRedeemed) {
          onRedeemed(couponId, response.data);
        }
      } else if (response && response.couponCode) {
        setRedeemResult(response);
        setDisplayedCouponCode(response.couponCode);
        if (onRedeemed) {
          onRedeemed(couponId, response);
        }
      } else {
        throw new Error(response?.message || 'Failed to redeem coupon.');
      }
    } catch (err) {
      console.error('Error redeeming coupon:', err);
      setRedeemError(err.message || 'Failed to redeem coupon. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const handleStart = (e) => {
    isDrawing.current = true;
    handleDraw(e);
  };

  const handleEnd = () => {
    isDrawing.current = false;
    checkScratchPercentage();
  };

  const handleDraw = (e) => {
    if (!isDrawing.current || scratched) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();
  };

  const checkScratchPercentage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imgData.data;
    let transparentCount = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) {
        transparentCount++;
      }
    }

    const totalPixels = pixels.length / 4;
    const percentScratched = (transparentCount / totalPixels) * 100;

    if (percentScratched > 40) {
      setScratched(true);
      if (!scratchedTriggered.current) {
        scratchedTriggered.current = true;
        triggerRedeemApi();
      }
      if (onComplete) {
        onComplete();
      }
    }
  };

  const copyToClipboard = () => {
    const codeToCopy = displayedCouponCode || redeemResult?.couponCode || couponCode;
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const activeCouponCode = displayedCouponCode || redeemResult?.couponCode || couponCode;

  return (
    <Box>
      {/* Card Outer */}
      <Box
        sx={{
          width: '100%',
          height: '240px',
          borderRadius: '20px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)',
          background: 'linear-gradient(135deg, #153A35 0%, #0F2E2B 100%)',
          border: '1.5px solid rgba(212, 162, 76, 0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Underlying Reward Details (Revealed) */}
        <Box
          sx={{
            textAlign: 'center',
            p: 3,
            width: '100%',
            opacity: (scratched || canvasLoaded) ? 1 : 0,
            transition: 'opacity 0.15s ease-out',
            animation: scratched ? 'scaleUp 0.5s ease-out' : 'none',
            '@keyframes scaleUp': {
              '0%': { transform: 'scale(0.8)', opacity: 0 },
              '100%': { transform: 'scale(1)', opacity: 1 }
            }
          }}
        >
          {redeeming ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={36} sx={{ color: '#E8C078' }} />
              <Typography variant="body2" sx={{ color: '#FAF3E6', fontWeight: 600 }}>
                Unlocking your reward...
              </Typography>
            </Box>
          ) : redeemError ? (
            <Box sx={{ textAlign: 'center', px: 2 }}>
              <Alert severity="error" sx={{ background: 'rgba(228,87,46,0.15)', color: '#FF7B54', mb: 1.5, py: 0.5, border: '1px solid rgba(228,87,46,0.3)' }}>
                {redeemError}
              </Alert>
              <Button
                size="small"
                variant="outlined"
                onClick={triggerRedeemApi}
                sx={{ color: '#E8C078', borderColor: '#E8C078', borderRadius: '8px' }}
              >
                Retry Redeem
              </Button>
            </Box>
          ) : redeemResult ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
              <EmojiEventsIcon sx={{ fontSize: 38, color: '#D4A24C' }} />
              {redeemResult.tier && (
                <Chip
                  label={`${redeemResult.tier} REWARD`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(212, 162, 76, 0.2)',
                    color: '#E8C078',
                    fontWeight: 700,
                    fontSize: '10px',
                    letterSpacing: '0.08em',
                  }}
                />
              )}
              {redeemResult.productItem && (
                <Typography variant="body1" sx={{ color: '#FAF3E6', fontWeight: 700, fontSize: '14.5px' }}>
                  Product Win : <Box component="span" sx={{ color: '#E8C078' }}>{redeemResult.productItem}</Box>
                </Typography>
              )}
              <Typography variant="body1" sx={{ color: '#FAF3E6', fontWeight: 700, fontSize: '15.5px' }}>
                Cash Back : <Box component="span" sx={{ color: '#2F8F76', fontWeight: 800 }}>₹{redeemResult.cashbackAmountPaise != null ? (redeemResult.cashbackAmountPaise / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : (prize || 0)}</Box>
              </Typography>
            </Box>
          ) : (
            <Box>
              <EmojiEventsIcon sx={{ fontSize: 44, color: '#D4A24C', mb: 1 }} />
              <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, color: '#FAF3E6', mb: 0.5 }}>
                {scratched ? 'Reward Unlocked!' : 'Keep Scratching...'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#D4A24C', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                {scratched ? 'Coupon Redeemed' : 'Scratch to reveal the price'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Overlay Scratch Canvas */}
        {!scratched && (
          <Box
            component="canvas"
            ref={canvasRef}
            onMouseDown={handleStart}
            onMouseMove={handleDraw}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchMove={handleDraw}
            onTouchEnd={handleEnd}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              cursor: scratched ? 'default' : 'crosshair',
              transition: 'opacity 0.4s ease-out',
              opacity: scratched ? 0 : 1,
              pointerEvents: scratched ? 'none' : 'auto',
              zIndex: 2,
            }}
          />
        )}
      </Box>

      {/* Action Row & Helper Instructions */}
      <Box sx={{ mt: 4 }}>
        {!scratched ? (
          <Typography variant="body1" sx={{ color: 'rgba(245, 241, 230, 0.7)', fontStyle: 'italic' }}>
            Drag or swipe across the card to scratch it!
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.6)', mb: 2 }}>
              Your reward is verified and processed to your account.
            </Typography>

            {/* Coupon Code copy bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: 'rgba(15, 46, 43, 0.6)',
                border: '1px solid rgba(245, 241, 230, 0.1)',
                borderRadius: '12px',
                p: 1.5,
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  color: '#FAF3E6',
                  fontSize: '13px',
                }}
              >
                {activeCouponCode}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy Code"}>
                <IconButton size="small" onClick={copyToClipboard} sx={{ color: '#D4A24C' }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={onGoBack}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  py: 1.4,
                  borderRadius: '999px',
                  boxShadow: '0 8px 24px rgba(212, 162, 76, 0.2)',
                  '&:hover': { backgroundColor: '#E8C078' },
                }}
              >
                Done
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.15)',
                  color: '#F5F1E6',
                  borderRadius: '999px',
                  px: 3,
                  '&:hover': {
                    borderColor: '#F5F1E6',
                    backgroundColor: 'rgba(245, 241, 230, 0.05)',
                  },
                }}
              >
                <ShareIcon fontSize="small" />
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
